import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { WrapUpEditor } from '../../components/portal/WrapUpEditor';
import { HINT } from '../../components/portal/WrapUpParts';
import { fetchWrapUpIndex, type WrapUpState } from '../../utils/wrapUp';
import { projectStatus } from '../../utils/status';
import { useOpenIdHistory } from '../../utils/studioViewHistory';

/**
 * WRAP-UPS — the post-show reports, across every project.
 *
 * It was a section on Project Admin for about an hour. Tom: *"I don't want
 * it under the projects - it's not easy to find."* Call Sheets made exactly
 * the same journey on 21 Sep for the same reason, and the lesson is the
 * same: a thing you do a handful of times a year is unfindable when it is
 * buried inside the screen for a thing you do every day.
 *
 * Being studio-wide earns its keep beyond findability — the list answers
 * "which finished projects still have no report?", which no per-project
 * screen ever could.
 *
 * FINISHED WORK FIRST. A wrap-up only appears on a client page once every
 * deliverable is signed off, so a project still in production is listed
 * underneath and marked, rather than hidden: writing one early is allowed,
 * it just will not show yet.
 *
 * **The row shape is the studio's, not this screen's (16 Oct 2026).** The
 * client is an eyebrow, the project takes the display size, the state sits
 * at the right edge. See components/portal/ProjectList.tsx for why — the
 * reasoning is written out once there and these four lists all follow it.
 */

const LIVE = 'Live';
const DRAFT = 'Draft';
function stateOf(state: WrapUpState | undefined): string {
  if (!state || !state.filled) return '';
  return state.published ? LIVE : DRAFT;
}
export function WrapUps() {
  const {
    activeProjects,
    shows
  } = usePortal();
  const [index, setIndex] = useState<Map<string, WrapUpState>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  useOpenIdHistory('wrapUpId', openId, setOpenId);

  /* Re-read on the way back from the editor, so a report just published
     does not still read "Draft" in the list behind it. */
  useEffect(() => {
    if (openId) return;
    let live = true;
    void fetchWrapUpIndex().then(result => {
      if (!live) return;
      setIndex(result);
      setLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [openId]);
  const rows = useMemo(() => {
    const showName = new Map(shows.map(show => [show.id, show.name]));
    return [...activeProjects].map(project => {
      const series = project.showId ? showName.get(project.showId) ?? '' : '';
      return {
        project,
        complete: projectStatus(project).toLowerCase() === 'complete',
        tag: stateOf(index.get(project.id)),
        /* A one-episode show is named after its only project, so printing
           both is the same words twice. */
        series: series && series !== project.name ? series : ''
      };
    }).sort((a, b) => {
      /* Written first, then finished-and-waiting, then the rest. */
      const rank = (row: {
        complete: boolean;
        tag: string;
      }) => row.tag ? 0 : row.complete ? 1 : 2;
      const byRank = rank(a) - rank(b);
      if (byRank !== 0) return byRank;
      return a.project.client.localeCompare(b.project.client);
    });
  }, [activeProjects, shows, index]);
  const open = openId ? activeProjects.find(row => row.id === openId) ?? null : null;
  const waiting = rows.filter(row => row.complete && !row.tag).length;
  if (open) {
    return <div data-id="element-2591" data-mp="1qhau3vuksue0">
        <button type="button" onClick={() => setOpenId(null)} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-2592" data-mp="1qhkk3jfmf1jp">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2593" data-mp="31whe77qnwgxx" />
          Wrap-ups
        </button>

        <p className="label mt-8 text-paper/40" data-id="element-2594" data-mp="1gf8kjfdxxs3e">{open.client}</p>
        <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2595" data-mp="1ldti171t5xo5">
          {open.name}
        </h1>
        <p className="label mt-6 text-paper/40" data-id="element-2596" data-mp="1ph7ytzxkmi2n">{projectStatus(open)}</p>

        <WrapUpEditor projectId={open.id} data-id="element-2597" data-mp="1udq4d20q6kg1" />
      </div>;
  }
  return <div data-id="element-2598" data-mp="1zdkrcuhdqpvt">
      <h1 className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2599" data-mp="1zd2p9h81dhfz">Wrap-ups</h1>
      <p className={HINT} data-id="element-2600" data-mp="244ayac5gf0sm">
        The post-show report a client sees on their page once the work is complete. Most projects
        never need one.
      </p>

      <section aria-labelledby="wrapup-list" className="mt-12" data-id="element-2601" data-mp="295gtqhqi125u">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-2602" data-mp="2d6kbj9s3x89p">
          <h2 id="wrapup-list" className="label m-0 text-paper/40" data-id="element-2603" data-mp="2i4vfx4zmuqkj">
            Projects
          </h2>
          {loaded && waiting > 0 && <p className="label-sm m-0 text-paper/35" data-id="element-2604" data-mp="1i5lzpawkpmk">{waiting} finished with no report</p>}
        </div>

        <ul className="m-0 mt-4 list-none p-0" data-id="element-2605" data-mp="3tl6lkf9bnuo1">
          {rows.map(({
          project,
          complete,
          tag,
          series
        }, index) => <li key={project.id} className="row-in border-b border-ruleDark" style={{
          ['--row' as string]: Math.min(index, 10)
        }} data-id="element-2606" data-mp="3onn2n21oitpr" data-mpi={index}>
              <button type="button" onClick={() => setOpenId(project.id)} className="group grid w-full grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:grid-cols-12" data-id="element-2607" data-mp="l5mthjke0iak">
                <span className="min-w-0 sm:col-span-9" data-id="element-2608" data-mp="bdz8a0efbxhj">
                  <span className="label block text-paper/50" data-id="element-2609" data-mp="18wit32v34hsd">{project.client}</span>
                  <span className="mt-2.5 block font-display text-2xl leading-tight sm:text-[1.75rem]" data-id="element-2610" data-mp="1447gazf8x1ie">
                    {project.name}
                  </span>
                  {series && <span className="label-sm mt-2 block text-paper/30" data-id="element-2611" data-mp="1s02quwvzw893">{series}</span>}
                </span>
                <span className="label flex items-center gap-4 sm:col-span-3 sm:justify-end" data-id="element-2612" data-mp="1e8xmxillgtsy">
                  {tag ? <span className={tag === LIVE ? 'text-paper' : 'text-paper/60'} data-id="element-2613" data-mp="1i9hiwiuflrdi">{tag}</span> : <span className="text-paper/30" data-id="element-2614" data-mp="149qcp4a5pfa2">
                      {complete ? 'No report' : projectStatus(project)}
                    </span>}
                  <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-2615" data-mp="vaboycldjit9" />
                </span>
              </button>
            </li>)}

          {activeProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-2616" data-mp="2tn3gmsecx1an">
              Nothing in production.
            </li>}
        </ul>
      </section>
    </div>;
}